
import React, { useState } from "react";

import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  createDM,
  createConnection,
  deleteConnection,
  respondConnection,
} from "../../api/api";
import { defaultPfpUrl, formatLastActive, isAccountVerified } from "../utils";
import ProfileLocationLink from "./ProfileLocationLink";
import ProfilePhotoModal from "./ProfilePhotoModal";
import { useNow } from "../../hooks/useNow";
import { usePresenceLastOnline } from "../../context/UserRealtimeContext";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { usePeerConnectionStatus } from "../../context/UserRealtimeContext";

const MapPinIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM12 11.5a2.5 2.5 0 000-5 2.5 2.5 0 000 5z"
    />
  </svg>
);

/**
 * Connect action on user profile.
 * States: Connect, Pending (disabled), Accept Connection, Disconnect.
 *
 * @param {Object} props
 * @param {Object} props.targetUser
 * @param {Object} props.selfUser
 * @returns {JSX.Element|null}
 */
const ConnectionButton = ({ targetUser, selfUser }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const { status: connectionStatus, connectionId, reportConnectionChange, refreshPendingConnections } =
    usePeerConnectionStatus(
      targetUser?.id,
      targetUser.user_connection_status || "none",
      targetUser.connection_id || 0
    );

  const targetName =
    targetUser?.first_name && targetUser?.last_name
      ? `${targetUser.first_name} ${targetUser.last_name}`
      : targetUser?.username || "this user";

  const handleCreateConnection = async () => {
    setIsConnecting(true);
    try {
      const result = await createConnection({
        sender_id: selfUser.id,
        receiver_id: targetUser.id,
      });
      if (result.success) {
        reportConnectionChange({
          peerUserId: targetUser.id,
          status: "pending_sent",
          connectionId: result.data?.id || 0,
        });
      }
    } catch (err) {
      console.error("Error connecting:", err);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleAcceptConnection = async () => {
    if (!connectionId) return;
    setIsConnecting(true);
    try {
      const result = await respondConnection({
        connection_id: connectionId,
        user_id: selfUser.id,
        status: "active",
      });
      if (result.success) {
        reportConnectionChange({
          peerUserId: targetUser.id,
          status: "active",
          connectionId,
        });
        await refreshPendingConnections();
      }
    } catch (err) {
      console.error("Error accepting connection:", err);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDeleteConnection = async () => {
    setIsConnecting(true);
    try {
      const result = await deleteConnection(selfUser.id, targetUser.id);
      if (result.success) {
        reportConnectionChange({
          peerUserId: targetUser.id,
          status: "none",
          connectionId: 0,
        });
        setConfirmDisconnect(false);
      }
    } catch (err) {
      console.error("Error deleting connection:", err);
    } finally {
      setIsConnecting(false);
    }
  };

  const getButtonState = () => {
    if (!selfUser || !targetUser || selfUser.id === targetUser.id) {
      return { callback: null, text: "", interactive: false, color: "accent" };
    }

    if (selfUser.type !== "user" || !isAccountVerified(selfUser)) {
      return { callback: null, text: "", interactive: false, color: "accent" };
    }

    if (connectionStatus === "active" || connectionStatus === "accepted") {
      return { callback: null, text: "", interactive: false, color: "accent" };
      // return {
      //   callback: () => setConfirmDisconnect(true),
      //   text: "Disconnect",
      //   interactive: true,
      //   color: "delete",
      // };
    }

    if (connectionStatus === "pending_sent" || connectionStatus === "pending") {
      return {
        callback: null,
        text: "Pending",
        interactive: false,
        color: "accent",
      };
    }

    if (connectionStatus === "pending_received") {
      return {
        callback: handleAcceptConnection,
        text: "Accept Connection",
        interactive: true,
        color: "accent",
      };
    }

    if (connectionStatus === "rejected" || connectionStatus === "left") {
      return { callback: null, text: "", interactive: false, color: "accent" };
    }

    return {
      callback: handleCreateConnection,
      text: "Connect",
      interactive: true,
      color: "accent",
    };
  };

  const buttonState = getButtonState();

  if (buttonState.text === "") {
    return null;
  }

  return (
    <>
      <Button
        type="button"
        onClick={buttonState.callback}
        disabled={isConnecting || !buttonState.interactive}
        title={buttonState.title || undefined}
        aria-label={buttonState.title || buttonState.text}
        variant={
          buttonState.interactive
            ? buttonState.color === "delete"
              ? "destructive"
              : "default"
            : "outline"
        }
        className="h-9"
      >
        {isConnecting ? "Loading…" : buttonState.text}
      </Button>
      <Dialog
        open={confirmDisconnect}
        onOpenChange={(open) => {
          if (!open && !isConnecting) setConfirmDisconnect(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Disconnect?</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove your connection with {targetName}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={isConnecting}
              onClick={() => setConfirmDisconnect(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isConnecting}
              onClick={handleDeleteConnection}
            >
              {isConnecting ? "Loading…" : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * @param {Object} props
 * @param {Object} props.user
 * @param {number} props.userId
 * @param {Object} props.selfUser
 * @param {number} props.targetUserId
 * @param {boolean} props.isEditing
 * @param {function} props.onEdit
 * @param {boolean} [props.reduceMotion]
 * @returns {JSX.Element}
 */
const UserProfileHeader = ({ user, userId, selfUser, targetUserId, isEditing, onEdit, reduceMotion }) => {
  const navigate = useNavigate();
  const [isCreatingDM, setIsCreatingDM] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const now = useNow();
  const lastOnline = usePresenceLastOnline(user?.id, user?.last_online);

  const displayName = (u) =>
    u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.username;
  const initials = (n) => n.replace(/\s+/g, " ").trim().slice(0, 2).toUpperCase() || "U";

  const handleMessage = async () => {
    if (!userId || !targetUserId || userId === targetUserId) return;

    setIsCreatingDM(true);
    try {
      const result = await createDM(userId, targetUserId);
      if (result.success && result.data?.id) {
        // Pass chat payload so MessagingPage can select immediately (existing or newly created).
        navigate(`/messaging/${result.data.id}`, { state: { chat: result.data } });
      }
    } catch (err) {
      console.error("Error creating DM:", err);
    } finally {
      setIsCreatingDM(false);
    }
  };

  return (
    <motion.div
      className="relative mx-auto w-full max-w-7xl overflow-x-clip bg-ui-card"
      {...(reduceMotion
        ? {}
        : {
            initial: { opacity: 0, y: 8 },
            animate: { opacity: 1, y: 0, transition: { duration: 0.26, ease: [0.25, 0.46, 0.45, 0.94] } },
          })}
    >
      <div className="w-full min-w-0 px-4 pt-8 pb-8">
        <div className="flex min-w-0 items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4 sm:gap-6">
            <div className="shrink-0">
              <button
                type="button"
                onClick={() => setPhotoOpen(true)}
                className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-card"
                aria-label={`View ${displayName(user)}'s profile photo`}
              >
                <Avatar className="h-20 w-20 border-4 border-ui-border shadow-profile-md sm:h-28 sm:w-28">
                  <AvatarImage
                    src={user.pfp_url || defaultPfpUrl}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultPfpUrl;
                    }}
                  />
                  <AvatarFallback className="text-2xl font-semibold text-text-secondary">
                    {initials(displayName(user))}
                  </AvatarFallback>
                </Avatar>
              </button>
              <ProfilePhotoModal
                open={photoOpen}
                onOpenChange={setPhotoOpen}
                src={user.pfp_url || defaultPfpUrl}
                alt={`${displayName(user)} profile photo`}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight text-text-primary sm:text-3xl">
                  {user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user.username}
                </h1>
                {userId === targetUserId && !isEditing && (
                  <Button type="button" variant="outline" size="sm" onClick={onEdit}>
                    Edit
                  </Button>
                )}
                {userId !== targetUserId && userId && selfUser?.type === "user" && isAccountVerified(selfUser) ? (
                  <Button
                    type="button"
                    onClick={handleMessage}
                    disabled={isCreatingDM}
                    className="h-9"
                  >
                    {isCreatingDM ? "Loading…" : "Message"}
                  </Button>
                ) : null}
                <ConnectionButton targetUser={user} selfUser={selfUser} />
              </div>
              <p className="mt-0.5 text-text-secondary">@{user.username}</p>
              <p className="mt-1 text-sm text-text-muted">
                {formatLastActive(lastOnline, now)}
              </p>
              <ProfileLocationLink
                label={user.location}
                latitude={user.latitude}
                longitude={user.longitude}
                className="mt-1 flex items-center gap-1.5 text-sm text-text-muted"
                icon={<MapPinIcon className="h-4 w-4 shrink-0" />}
              />
            </div>
          </div>

          {user.affiliations && user.affiliations.length > 0 && (
            <div className="hidden text-right sm:block">
              <div className="flex flex-col items-end space-y-2">
                {user.affiliations
                  .filter((aff) => aff.status === "active")
                  .slice(0, 2)
                  .map((aff) => (
                    <Link
                      key={aff.organization.id}
                      to={`/organization/${aff.organization.id}`}
                      className="flex items-center space-x-3 transition-opacity hover:opacity-80"
                    >
                      <div className="text-right">
                        {aff.role && (
                          <p className="text-sm font-medium text-text-primary">
                            {aff.role} at
                          </p>
                        )}
                        <p className="text-base font-semibold text-accent transition-colors hover:text-text-accent-hover">
                          {aff.organization.name}
                        </p>
                      </div>
                      <div className="shrink-0">
                        <Avatar className="h-16 w-16 border-2 border-ui-border shadow-sm">
                          <AvatarImage
                            src={aff.organization.pfp_url || defaultPfpUrl}
                            alt={`${aff.organization.name} logo`}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = defaultPfpUrl;
                            }}
                          />
                          <AvatarFallback className="text-sm">
                            {aff.organization.name?.slice(0, 2) ?? "O"}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default UserProfileHeader;
